import { Icon } from "@iconify/react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "use-intl";
import { getAppwriteClient } from "$/modules/appwrite/lib/appwrite";
import { buckets } from "$/modules/appwrite/lib/appwrite-buckets";
import {
	listParticipantExamHistory,
	type ParticipantExamHistoryItem,
} from "$/modules/exam/lib/exam";
import { cn } from "$/modules/shared/lib/utils";
import { Badge } from "$/modules/shared/ui/badge";
import { Button } from "$/modules/shared/ui/button";
import { Card, CardContent } from "$/modules/shared/ui/card";
import { ConstructionHeader } from "$/modules/shared/ui/construction-header";
import { PageContainer } from "$/modules/shared/ui/page-container";

export const Route = createFileRoute("/$locale/$userId/profile/certificates/")({
	component: CertificatesPage,
});

function CertificatesPage() {
	const t = useTranslations("exam");
	const tCommon = useTranslations("common");
	const { locale, userId } = Route.useParams();
	const [history, setHistory] = useState<ParticipantExamHistoryItem[]>([]);
	const [error, setError] = useState("");
	const dateFormatter = useMemo(
		() => new Intl.DateTimeFormat(locale === "en" ? "en-US" : "de-DE"),
		[locale],
	);

	useEffect(() => {
		void listParticipantExamHistory()
			.then(setHistory)
			.catch((cause) =>
				setError(
					cause instanceof Error ? cause.message : t("historyLoadError"),
				),
			);
	}, [t]);

	return (
		<PageContainer>
			<ConstructionHeader
				titleKey="exam.history"
				description={t("historyDescription")}
			/>
			<div className="grid gap-4 md:grid-cols-2">
				{history.length === 0 && !error && (
					<p className="text-sm text-foreground-500">{t("noHistory")}</p>
				)}
				{history.map(({ attempt, certificate, project }) => {
					const certificateDownloadUrl =
						certificate?.status === "issued" && certificate.fileId
							? getAppwriteClient().storage.getFileDownload(
									buckets.certificates,
									certificate.fileId,
								)
							: null;
					return (
						<Card key={attempt.$id} className="border border-default-200">
							<CardContent className="gap-4 p-5">
								<div className="flex items-start justify-between gap-3">
									<div>
										<p className="font-display text-xl font-bold">
											{project.name}
										</p>
										<p className="text-sm text-foreground-500">
											{t("completedAt", {
												date: dateFormatter.format(
													new Date(attempt.submittedAt ?? attempt.$updatedAt),
												),
											})}
										</p>
									</div>
									<Badge
										variant="secondary"
										className={cn({
											"bg-success/15 text-success":
												(attempt.passed ? "success" : "danger") === "success",
											"bg-danger/15 text-danger":
												(attempt.passed ? "success" : "danger") === "danger",
										})}
									>
										{attempt.passed ? t("passed") : t("failed")}
									</Badge>
								</div>
								<p className="text-sm text-foreground-600">
									{t("score", {
										score: attempt.score ?? 0,
										maxScore: attempt.maxScore ?? 0,
										percentage: attempt.percentage ?? 0,
									})}
								</p>
								{certificateDownloadUrl ? (
									<Button variant="default" asChild>
										<a href={certificateDownloadUrl}>
											{<Icon icon="lucide:download" />}
											{t("downloadCertificate")}
										</a>
									</Button>
								) : (
									<p className="text-sm text-foreground-500">
										{certificate ? t("certificatePending") : t("noCertificate")}
									</p>
								)}
							</CardContent>
						</Card>
					);
				})}
			</div>
			{error && <p className="mt-4 text-sm text-danger">{error}</p>}
			<div className="mt-12">
				<Button asChild variant="muted">
					<Link to="/$locale/$userId/projects" params={{ locale, userId }}>
						{<Icon icon="lucide:arrow-left" className="size-5" />}
						{tCommon("backToDashboard")}
					</Link>
				</Button>
			</div>
		</PageContainer>
	);
}
